import {
  Box,
  Flex,
  HStack,
  Spinner,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router";
import CDS from "design-system";

import AppPage from "layouts/AppPage";
import { generatePath } from "helpers/url";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import FormHeaderStrip from "../Components/FormHeaderStrip";
import ResponsesGrid from "../Components/ResponsesGrid";
import RecipientsTab from "../Components/RecipientsTab";
import AssignFormPanel from "../Components/AssignFormPanel";
import FieldCanvas from "../Components/Builder/FieldCanvas";
import FieldInspector from "../Components/Builder/FieldInspector";
import { useCustomFormQuery, useFormAssignmentsQuery } from "../hooks";
import useCustomFormEditorState from "../hooks/useCustomFormEditorState";
import useKeyboardReorder from "../hooks/useKeyboardReorder";

export default function CustomFormShowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: form, isLoading } = useCustomFormQuery(id);
  const { data: assignments } = useFormAssignmentsQuery(form?.id);
  const { isOpen: isAssignOpen, onOpen: openAssign, onClose: closeAssign } = useDisclosure();
  const {
    fields,
    instructions,
    autosave,
    selectedIndex,
    setSelectedIndex,
    setField,
    reorder,
    duplicate,
    removeField,
    addFieldOfType,
    setInstructions,
    persistName,
    responseScreenHeader,
    responseScreenText,
    setResponseScreenHeader,
    setResponseScreenText,
    showSubmitterInfo,
    setShowSubmitterInfo,
  } = useCustomFormEditorState({ form, id });

  const openPreview = () =>
    navigate(generatePath({ path: PEOPLE_TEAM_ROUTES.customForms.preview, params: { id: String(id) } }));

  useKeyboardReorder({
    selectedIndex,
    fieldCount: fields.length,
    onSwap: (fromIndex, toIndex) => {
      reorder(fromIndex, toIndex);
      setSelectedIndex(toIndex);
    },
  });

  const responsesCount = form?.responsesCount ?? 0;

  if (isLoading || !form) {
    return (
      <AppPage>
        <Box p={6}>{isLoading ? <Spinner /> : <CDS.Text14>Form not found.</CDS.Text14>}</Box>
      </AppPage>
    );
  }

  const selectedField = selectedIndex != null ? fields[selectedIndex] ?? null : null;

  return (
    <AppPage>
      <Box p={6}>
        <FormHeaderStrip
          form={form}
          autosave={autosave}
          onNameChange={persistName}
          onPreview={openPreview}
          onAssign={openAssign}
        />

        <Tabs
          variant="line"
          isLazy
          sx={{
            "button[aria-selected=true]": {
              color: "cdsIndigo.500",
              borderColor: "cdsIndigo.500",
            },
            "button:hover": { color: "cdsIndigo.500" },
          }}
        >
          <TabList borderBottomColor="border.subtle">
            <Tab color="content.secondary">Fields</Tab>
            <Tab color="content.secondary">
              Responses
              {responsesCount > 0 && (
                <CDS.Text12 as="span" ml={2} color="content.tertiary" fontWeight="normal">
                  {responsesCount}
                </CDS.Text12>
              )}
            </Tab>
            <Tab color="content.secondary">
              Recipients
              {(assignments?.length ?? 0) > 0 && (
                <CDS.Text12 as="span" ml={2} color="content.tertiary" fontWeight="normal">
                  {assignments?.length}
                </CDS.Text12>
              )}
            </Tab>
            <Tab color="content.secondary">Settings</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <Flex justify="center" pt={4}>
                <FieldCanvas
                  fields={fields}
                  instructions={instructions}
                  responseScreenHeader={responseScreenHeader || null}
                  responseScreenText={responseScreenText || null}
                  selectedIndex={selectedIndex}
                  onInstructionsChange={setInstructions}
                  onResponseScreenHeaderChange={setResponseScreenHeader}
                  onResponseScreenTextChange={setResponseScreenText}
                  onAddField={addFieldOfType}
                  onSelect={setSelectedIndex}
                  onReorder={reorder}
                  onDuplicate={duplicate}
                  onDelete={removeField}
                />
              </Flex>
              <FieldInspector
                field={selectedField}
                isOpen={selectedIndex != null}
                onClose={() => setSelectedIndex(null)}
                onFieldChange={(patch) => selectedIndex != null && setField(selectedIndex, patch)}
              />
            </TabPanel>

            <TabPanel px={0}>
              <ResponsesGrid form={form} />
            </TabPanel>

            <TabPanel px={0}>
              <RecipientsTab form={form} />
            </TabPanel>

            <TabPanel px={0}>
              <HStack justify="space-between" align="flex-start" pt={4} maxW="560px" spacing={6}>
                <Box>
                  <CDS.Text14 fontWeight="bold" color="content.primary">Show submitter info</CDS.Text14>
                  <CDS.Text12 color="content.tertiary">
                    Displays &quot;Submitting as {"{email}"}&quot; next to the Submit button and in the
                    submit confirmation.
                  </CDS.Text12>
                </Box>
                <Switch
                  aria-label="Show submitter info"
                  isChecked={showSubmitterInfo}
                  onChange={(e) => setShowSubmitterInfo(e.target.checked)}
                  mt={1}
                />
              </HStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      <AssignFormPanel
        form={form}
        isOpen={isAssignOpen}
        onClose={closeAssign}
        existingAssignments={assignments ?? []}
      />
    </AppPage>
  );
}
